"use client"

import React, { useState, useEffect } from "react"
import { useParams } from "react-router-dom"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, IconButton, TextField, Select } from "@radix-ui/themes"
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search,
  Filter,
  MoreVertical,
  DollarSign,
  Tag,
  X
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import RestaurantFoodItemModal from "../../components/RestaurantFoodItemModal"
import { useLanguage } from "../../contexts/LanguageContext"
import PageHeader from "../../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

// Use the same FoodItem interface as the modal
interface FoodItem {
  _id: string
  name: string
  description: string
  price: number
  image: string
  category: string
  restaurant: string
  isAvailable: boolean
  isFeatured?: boolean
  isPopular?: boolean
  preparationTime: string
  originalPrice?: number
  ingredients: string[]
  allergens: string[]
  nutritionalInfo: {
    calories?: number
    protein?: number
    carbs?: number
    fat?: number
  }
  tags: string[]
  createdAt: string
  updatedAt: string
}



const RestaurantFoodItemsPage: React.FC = () => {
  const { restaurantId } = useParams()
  const { t, language, dir } = useLanguage()
  const [foodItems, setFoodItems] = useState<FoodItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [availabilityFilter, setAvailabilityFilter] = useState("all")
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<FoodItem | null>(null)

  // Categories for the filter dropdown
  const categories = [
    { label: t("allCategories"), value: "all" },
    { label: t("appetizer"), value: "appetizer" },
    { label: t("mainDish"), value: "main-dish" },
    { label: t("sideDish"), value: "side-dish" },
    { label: t("sandwich"), value: "sandwich" },
    { label: t("desserts"), value: "dessert" },
    { label: t("beverage"), value: "beverage" },
    { label: t("drink"), value: "drink" }
  ]

  useEffect(() => {
    fetchFoodItems()
  }, [restaurantId])

  const fetchFoodItems = async () => {
    try {
      setLoading(true)
      // Set a high limit to get all food items
      const response = await restaurantPortalAPI.getFoodItems(restaurantId!, { limit: 1000 })
      setFoodItems(response.data.foodItems || [])
    } catch (error) {
      console.error("Error fetching food items:", error)
      setFoodItems([])
    } finally {
      setLoading(false)
    }
  }

  const handleAvailabilityToggle = async (itemId: string, currentStatus: boolean) => {
    try {
      await restaurantPortalAPI.updateFoodItemAvailability(itemId, {
        isAvailable: !currentStatus
      })
      setFoodItems(items =>
        items.map(item =>
          item._id === itemId
            ? { ...item, isAvailable: !currentStatus }
            : item
        )
      )
    } catch (error) {
      console.error("Error updating availability:", error)
    }
  }

  const handleEdit = (item: FoodItem) => {
    setEditingItem(item)
    setShowModal(true)
  }

  const handleDelete = async (itemId: string) => {
    if (window.confirm(t("deleteFoodItemConfirm"))) {
      try {
        await restaurantPortalAPI.deleteFoodItem(itemId)
        setFoodItems(items => items.filter(item => item._id !== itemId))
      } catch (error) {
        console.error("Error deleting food item:", error)
      }
    }
  }

  const handleModalSuccess = () => {
    fetchFoodItems()
  }

  const filteredItems = (foodItems || []).filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter
    const matchesAvailability = availabilityFilter === "all" || 
                              (availabilityFilter === "available" && item.isAvailable) ||
                              (availabilityFilter === "unavailable" && !item.isAvailable)
    
    return matchesSearch && matchesCategory && matchesAvailability
  })

  // Get unique categories from existing food items
  const existingCategories = (foodItems || []).reduce((acc: string[], item) => {
    if (!acc.includes(item.category)) {
      acc.push(item.category)
    }
    return acc
  }, [])

  if (loading) {
    return <LoadingSpinner size="3" text={t("loadingFoodItems")} />
  }

  return (
    <div dir={dir}>
      <Flex direction="column" gap="6">
        {/* Header */}
      <PageHeader
        title={t("foodItems")}
        subtitle={t("manageFoodItems")}
        action={
          <Button onClick={() => setShowModal(true)} size="3">
            <Plus size={16} />
            {t("addFoodItem")}
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <Grid columns={{ initial: "1", md: "4" }} gap="4" align="center">
          <TextField.Root
            placeholder={t("searchFoodItems")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          >
            <TextField.Slot>
              <Search size={16} />
            </TextField.Slot>
          </TextField.Root>
          
          <Select.Root value={categoryFilter} onValueChange={setCategoryFilter}>
            <Select.Trigger style={{ width: "100%" }} />
            <Select.Content>
              {categories.map(category => (
                <Select.Item key={category.value} value={category.value}>{category.label}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          <Select.Root value={availabilityFilter} onValueChange={setAvailabilityFilter}>
            <Select.Trigger style={{ width: "100%" }} />
            <Select.Content>
              <Select.Item value="all">{t("allItems")}</Select.Item>
              <Select.Item value="available">{t("available")}</Select.Item>
              <Select.Item value="unavailable">{t("unavailable")}</Select.Item>
            </Select.Content>
          </Select.Root>

          <Text size="2" color="gray">
            {t("itemsCount", { count: filteredItems.length, total: foodItems.length })}
          </Text>
        </Grid>
      </Card>

      {/* Food Items Grid */}
      <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="4">
        {filteredItems.map((item) => (
          <Card key={item._id}>
            <div className="relative">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                  <Tag className="w-8 h-8 text-gray-400" />
                </div>
              )}
              
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => handleAvailabilityToggle(item._id, item.isAvailable)}
                  className={`p-2 rounded-full ${
                    item.isAvailable 
                      ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                  }`}
                >
                  {item.isAvailable ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900 truncate">{item.name}</h3>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item._id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {item.description}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center text-green-600 font-semibold">
                  <DollarSign className="w-4 h-4 mr-1" />
                  {item.price.toFixed(2)}
                </div>
                <span className="text-xs text-gray-500 capitalize">{item.category}</span>
              </div>

              {item.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {item.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                  {item.tags.length > 3 && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                      +{item.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </Grid>

      {filteredItems.length === 0 && !loading && (
        <Card>
          <div className="text-center py-8">
            <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t("noFoodItemsFound")}</h3>
            <p className="text-gray-600 mt-2">
              {searchTerm || categoryFilter !== "all" || availabilityFilter !== "all"
                ? t("tryAdjustingFilters")
                : t("getStartedAddingFood")}
            </p>
            {!searchTerm && categoryFilter === "all" && availabilityFilter === "all" && (
              <Button onClick={() => setShowModal(true)} size="3" className="mt-4">
                <Plus size={16} />
                {t("addFoodItem")}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Restaurant Food Item Modal */}
      <RestaurantFoodItemModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingItem(null)
        }}
        foodItem={editingItem}
        mode={editingItem ? 'edit' : 'create'}
        restaurantId={restaurantId!}
        onSuccess={handleModalSuccess}
      />
      </Flex>
    </div>
  )
}

export default RestaurantFoodItemsPage
